import { Link, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LogIn, LogOut, Globe2, Menu, X, Settings } from "lucide-react";
import { useState } from "react";

function Logo({ className = "h-11 md:h-12" }) {
  const { settings } = useApp();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  let src = settings?.logo_url || "/brand/lepo-gete.png";
  if (src.startsWith("/api/")) src = `${BACKEND_URL}${src}`;
  return (
    <img
      src={src}
      alt={settings?.org_name || "Lepo Gete"}
      className={`${className} w-auto object-contain`}
      draggable={false}
      onError={(e) => { e.target.src = "/brand/lepo-gete.png"; }}
    />
  );
}

export default function Nav() {
  const { t, lang, toggleLang, user, logout, settings } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const s = settings || {};
  const pick = (key, fallback) => s[`${key}_${lang}`] || fallback;
  const items = [
    { to: "/", label: pick("nav_dashboard", t.nav.dashboard), tid: "nav-dashboard" },
    { to: "/beneficiaries", label: pick("nav_beneficiaries", t.nav.beneficiaries), tid: "nav-beneficiaries" },
    { to: "/resources", label: pick("nav_resources", t.nav.resources), tid: "nav-resources" },
    { to: "/impact", label: pick("nav_impact", "Dampak"), tid: "nav-impact" },
    { to: "/submit", label: pick("nav_submit", t.nav.submit), tid: "nav-submit" },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur" style={{ backgroundColor: "color-mix(in srgb, var(--cream) 95%, transparent)" }}>
      <div className="border-b" style={{ borderColor: "color-mix(in srgb, var(--ink) 15%, transparent)" }}>
        <div className="mx-auto max-w-7xl px-4 md:px-8 py-3 flex items-center gap-6">
          <Link to="/" className="shrink-0" data-testid="nav-logo" aria-label="Home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.tid}
                className={({ isActive }) =>
                  `px-4 py-2 font-semibold text-sm tracking-wide transition-colors ${
                    isActive ? "text-[#670003]" : "text-[#141414]/70 hover:text-[#141414]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={toggleLang}
              data-testid="nav-lang-toggle"
              className="flat-btn bg-transparent rounded-full px-3 py-1.5 flex items-center gap-2 text-sm font-semibold"
              aria-label="Toggle language"
            >
              <Globe2 className="w-4 h-4" strokeWidth={2} />
              {lang.toUpperCase()}
            </button>

            {user ? (
              <>
                <Link
                  to="/admin"
                  data-testid="nav-settings"
                  className="flat-btn bg-transparent rounded-full px-3 py-1.5 flex items-center gap-2 text-sm font-semibold"
                  aria-label="Settings"
                >
                  <Settings className="w-4 h-4" strokeWidth={2} />
                  <span className="hidden sm:inline">Pengaturan</span>
                </Link>
                <button
                  onClick={() => { logout(); navigate("/"); }}
                  data-testid="nav-logout"
                  className="flat-btn-primary rounded-full px-4 py-1.5 flex items-center gap-2 text-sm font-semibold"
                >
                  <LogOut className="w-4 h-4" strokeWidth={2} />
                  <span className="hidden sm:inline">{t.nav.logout}</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="flat-btn bg-transparent rounded-full px-4 py-1.5 flex items-center gap-2 text-sm font-semibold"
              >
                <LogIn className="w-4 h-4" strokeWidth={2} />
                <span className="hidden sm:inline">{t.nav.login}</span>
              </Link>
            )}

            <button
              className="lg:hidden flat-btn bg-transparent rounded-full p-2"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#141414]/15 mx-auto max-w-7xl px-4 py-2 flex flex-col">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`${it.tid}-mobile`}
                className={({ isActive }) =>
                  `px-3 py-3 border-b border-[#141414]/10 last:border-0 font-semibold ${
                    isActive ? "text-[#670003]" : "text-[#141414]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
